import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconPlus,
  IconUsers,
  IconUserMinus,
  IconReport,
  IconCreditCard,
  IconReceipt,
  IconMail,
  IconCalendar
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../../utils/constants';

const GroupsListScreen = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [sortBy, setSortBy] = useState('group_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [groupTypes, setGroupTypes] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Separate useEffect for initial load only
  useEffect(() => {
    console.log('GroupsListScreen initial useEffect triggered');
    
    // Reset selected group when page loads
    setSelectedGroup(null);
    
    fetchGroupTypes();
    fetchGroups();
  }, []);

  // Separate useEffect for search/sort changes
  useEffect(() => {
    if (searchTerm || sortBy || sortOrder) {
      fetchGroups();
    }
  }, [currentPage, sortBy, sortOrder, searchTerm]);

  const fetchGroupTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/group-types`);
      if (response.ok) {
        const data = await response.json();
        setGroupTypes(data);
      }
    } catch (error) {
      console.error('Error fetching group types:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/groups?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      setGroups(data.groups);
      setTotalPages(data.pagination.totalPages);
      setTotalGroups(data.pagination.totalGroups);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Error fetching groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setActiveTab('details');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleEdit = (group) => {
    setEditingGroup({ ...group });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/groups/${editingGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingGroup),
      });

      if (!response.ok) {
        throw new Error('Failed to update group');
      }

      const updatedGroup = await response.json();
      setGroups(prev => prev.map(group => 
        group.id === updatedGroup.group.id ? updatedGroup.group : group
      ));
      setShowEditModal(false);
      setEditingGroup(null);
      notifications.show({
        title: 'Success',
        message: 'Group updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating group:', error);
      setError('Error updating group. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingGroup(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingGroup(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await fetch(`${API_URL}/groups/${groupToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete group');
      }

      setShowDeleteModal(false);
      setGroupToDelete(null);
      fetchGroups();
      notifications.show({
        title: 'Success',
        message: 'Group deleted successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting group:', error);
      setError('Error deleting group. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (loading && groups.length === 0) {
    return (
      <Container fluid p={0} style={{ height: '100vh' }}>
        <Group justify="center" style={{ height: '100%' }}>
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Groups</Title>
            
            {/* Primary Actions */}
              <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => window.location.href = '/dashboard/groups-find'}
              leftSection={<IconSearch size="0.8rem" />}
              >
                Find
              </Button>
              <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
              onClick={() => window.location.href = '/dashboard/groups-add'}
              >
                Add
              </Button>
              <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
                onClick={() => selectedGroup && handleEdit(selectedGroup)}
                disabled={!selectedGroup}
              leftSection={<IconEdit size="0.8rem" />}
              >
                Change
              </Button>
              <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
                onClick={() => selectedGroup && handleDelete(selectedGroup)}
                disabled={!selectedGroup}
              leftSection={<IconTrash size="0.8rem" />}
              >
                Delete
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Group Management Actions */}
              <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              leftSection={<IconUsers size="0.8rem" />}
              >
                Add Member
              </Button>
              <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              leftSection={<IconUserMinus size="0.8rem" />}
              >
                Remove Member
              </Button>
              <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
              >
                Group Report
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Financial Actions */}
              <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
              >
                Post Charge
              </Button>
              <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
              >
                Payment
              </Button>
              <Button 
              variant="subtle" 
              color="pink"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
              >
                Refund
              </Button>
              
            <Divider color="gray.2" my="sm" />
              
            {/* Communication and Reports */}
              <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconMail size="0.8rem" />}
              >
                Emails
              </Button>
              <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
              >
                Reports
              </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Groups List</Title>
            </Group>

          {error && (
              <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md">
                <TextInput
                  placeholder="Search groups by name, number, or city..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => setSearchTerm('')} disabled={!searchTerm}>
                  Clear
                </Button>
                <Button color="green" leftSection={<IconPlus size="1rem" />}>
                  Add Group
                </Button>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th onClick={() => handleSort('group_number')} style={{ cursor: 'pointer' }}>
                        Group # {renderSortIcon('group_number')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        Name {renderSortIcon('name')}
                    </Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
                        City {renderSortIcon('city')}
                    </Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th>Market</Table.Th>
                    <Table.Th onClick={() => handleSort('active')} style={{ cursor: 'pointer' }}>
                        Status {renderSortIcon('active')}
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {groups.map((group) => (
                    <Table.Tr 
                        key={group.id}
                        onClick={() => handleGroupSelect(group)}
                        style={{ 
                          cursor: 'pointer',
                        backgroundColor: selectedGroup && selectedGroup.id === group.id ? '#ffff00' : '',
                          border: selectedGroup && selectedGroup.id === group.id ? '2px solid #ff0000' : ''
                        }}
                      >
                      <Table.Td><strong>{group.group_number}</strong></Table.Td>
                      <Table.Td>{group.name || 'N/A'}</Table.Td>
                      <Table.Td>{group.group_type_name || 'N/A'}</Table.Td>
                      <Table.Td>{group.city || 'N/A'}</Table.Td>
                      <Table.Td>{group.state || 'N/A'}</Table.Td>
                      <Table.Td>{group.market || 'N/A'}</Table.Td>
                      <Table.Td>
                        <Badge color={group.active ? 'green' : 'red'}>
                            {group.active ? 'Active' : 'Inactive'}
                          </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon 
                            color="orange" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleEdit(group); }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon 
                            color="red" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDelete(group); }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
                </Table>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}
            </Paper>

            {/* Group Details Section */}
          {selectedGroup && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="members">Members</Tabs.Tab>
                    <Tabs.Tab value="reservations">Reservations</Tabs.Tab>
                    <Tabs.Tab value="notes">Notes</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Group Number:</strong> {selectedGroup.group_number}</Text>
                          <Text><strong>Name:</strong> {selectedGroup.name || 'N/A'}</Text>
                          <Text><strong>Type:</strong> {selectedGroup.group_type_name || 'N/A'}</Text>
                          <Text><strong>Market:</strong> {selectedGroup.market || 'N/A'}</Text>
                          <Text><strong>Status:</strong> 
                            <Badge color={selectedGroup.active ? 'green' : 'red'} ml="xs">
                                  {selectedGroup.active ? 'Active' : 'Inactive'}
                                </Badge>
                          </Text>
                          <Text><strong>Phone 1:</strong> {selectedGroup.phone1 || 'N/A'}</Text>
                          <Text><strong>Phone 2:</strong> {selectedGroup.phone2 || 'N/A'}</Text>
                          <Text><strong>Email:</strong> {selectedGroup.email || 'N/A'}</Text>
                          <Text><strong>Create Date:</strong> {selectedGroup.create_date ? new Date(selectedGroup.create_date).toLocaleDateString() : 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Address 1:</strong> {selectedGroup.address1 || 'N/A'}</Text>
                          <Text><strong>Address 2:</strong> {selectedGroup.address2 || 'N/A'}</Text>
                          <Text><strong>City:</strong> {selectedGroup.city || 'N/A'}</Text>
                          <Text><strong>State:</strong> {selectedGroup.state || 'N/A'}</Text>
                          <Text><strong>Zipcode:</strong> {selectedGroup.zipcode || 'N/A'}</Text>
                          <Text><strong>Country:</strong> {selectedGroup.country || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Tabs.Panel>

                  <Tabs.Panel value="members" pt="md">
                    <Text>Group Members</Text>
                    <Text size="sm" c="dimmed">This tab will contain member information, roles, and permissions.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="reservations" pt="md">
                    <Text>Group Reservations</Text>
                    <Text size="sm" c="dimmed">This tab will contain reservation history, upcoming stays, and booking information.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="notes" pt="md">
                    <Text>Internal Notes</Text>
                    <Text size="sm" c="dimmed">This tab will contain internal notes, communication history, and important information.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Edit Group Modal */}
      <Modal opened={showEditModal} onClose={handleCancelEdit} title="Edit Group" size="lg">
          {editingGroup && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Group Number"
                      value={editingGroup.group_number}
                      disabled
                  description="Group number cannot be changed"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Group Type"
                      name="group_type_id"
                      value={editingGroup.group_type_id || ''}
                  onChange={(value) => setEditingGroup(prev => ({ ...prev, group_type_id: value }))}
                  data={groupTypes.map(type => ({ value: type.id.toString(), label: type.name }))}
                  placeholder="Select Group Type"
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Name"
                      name="name"
                      value={editingGroup.name || ''}
                      onChange={handleInputChange}
                      required
                    />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Market"
                      name="market"
                      value={editingGroup.market || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 1"
                      name="address1"
                      value={editingGroup.address1 || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 2"
                      name="address2"
                      value={editingGroup.address2 || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="City"
                      name="city"
                      value={editingGroup.city || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="State"
                      name="state"
                      value={editingGroup.state || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="Zipcode"
                      name="zipcode"
                      value={editingGroup.zipcode || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Country"
                      name="country"
                      value={editingGroup.country || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Status"
                  name="active"
                        type="checkbox"
                        checked={editingGroup.active}
                        onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 1"
                      name="phone1"
                      value={editingGroup.phone1 || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 2"
                      name="phone2"
                      value={editingGroup.phone2 || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Email"
                      name="email"
                      value={editingGroup.email || ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Create Date"
                  name="create_date"
                      type="date"
                      value={editingGroup.create_date ? editingGroup.create_date.split('T')[0] : ''}
                      onChange={handleInputChange}
                    />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCancelEdit}>
            Cancel
          </Button>
              <Button onClick={handleSaveEdit}>
            Save Changes
          </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Stack gap="md">
          <Text>
          Are you sure you want to delete group <strong>{groupToDelete?.group_number}</strong> ({groupToDelete?.name})?
          This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
              color="red"
            onClick={confirmDelete}
              loading={deleteLoading}
          >
              Delete
          </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default GroupsListScreen;
